// Review Types
export interface Review {
  reviewId: string;
  productSku: string;
  productName: string;
  customerNumber: string;
  userName: string;
  rating: number;
  title: string;
  content: string;
  productVintage?: string;
  verified: boolean;
  status: ReviewStatus;
  moderationStatus: ModerationStatus;
  flags: ReviewFlag[];
  createdAt: string;
  updatedAt: string;
  autoApproved: boolean;
  moderatedAt?: string;
  moderatedBy?: string;
  moderatorNotes?: string;
}

export enum ReviewStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
}

export enum ModerationStatus {
  Clean = 'Clean',
  PreFlagged = 'PreFlagged',
  Flagged = 'Flagged',
}

export interface ReviewFlag {
  type: FlagType;
  confidence: number;
  details?: string;
}

export enum FlagType {
  Profanity = 'Profanity',
  Spam = 'Spam',
  OffTopic = 'OffTopic',
  PersonalInfo = 'PersonalInfo',
  Harassment = 'Harassment',
}

// API Types
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ReviewFilters {
  status?: ReviewStatus[];
  moderationStatus?: ModerationStatus[];
  approvalFilter?: string[];
  flagTypes?: FlagType[];
  rating?: number[];
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface ReviewListParams extends ReviewFilters {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ModerateReviewRequest {
  reviewId: string;
  action: 'approve' | 'reject' | 'unapprove';
  notes?: string;
}

export interface BulkModerateRequest {
  reviewIds: string[];
  action: 'approve' | 'reject' | 'unapprove';
  notes?: string;
}

export interface BulkModerateResponse {
  successful: string[];
  failed: Array<{ reviewId: string; error: string }>;
}

// Veranstaltungen (Events) Types
export interface DepotRef {
  key: string; // commercetools Store-key, e.g. "D001"
  uuid: string; // commercetools Store-id
  /**
   * Depot name, e.g. "Köln Zentrum". Optional: events stored before the name was
   * carried along, and the JWT depot claim, only have the key. Render it via
   * formatDepot() so those cases degrade to the plain key.
   */
  name?: string;
}

export enum EventStatus {
  Initial = 'initial', // In Erstellung
  Rejected = 'rejected', // Nicht freigegeben
  InReview = 'in_review', // Zur Prüfung weitergeleitet
  Approved = 'approved', // Freigegeben
  Online = 'online', // Online
  Completed = 'completed', // Stattgefunden — Voraussetzung für die Abrechnung
  Cancelled = 'cancelled', // Abgesagt
}

export enum EventOrigin {
  Partner = 'partner',
  Central = 'central',
}

export enum EventAudience {
  Public = 'public', // öffentlich — für jeden Besucher
  Closed = 'closed', // geschlossen — nur Kunden mit zuvor erworbenem Ticket
}

export type LocationType = 'depot' | 'external' | 'online';

// Kostenbeitrag
export type PriceUnit = 'package' | 'person';

/**
 * MwSt.-Kategorie, wie in den commercetools tax-categories gepflegt.
 *
 * Die Keys sind NICHT nach Satz sortiert: A0 = 0 %, A1 = 19 %, A2 = 7 %.
 * Deshalb gibt TAX_CATEGORY_OPTIONS die Anzeigereihenfolge vor.
 */
export type TaxCategoryKey = 'A0' | 'A1' | 'A2';

/** Auswahl-Reihenfolge im Formular: aufsteigend nach Steuersatz. */
export const TAX_CATEGORY_OPTIONS: readonly TaxCategoryKey[] = ['A0', 'A2', 'A1'];

/**
 * "Anlass der Veranstaltung" — nur bei Teilnehmerkreis "geschlossen" im
 * Formular sichtbar (15.09.2026). Bewusst numerische String-Keys statt eines
 * literal-Unions (Nutzer-Vorgabe: "erstmal") — ein neuer Anlass braucht nur
 * einen weiteren Eintrag hier + einen i18n-Key `eventReason.<key>`.
 */
export const EVENT_REASON_OPTIONS = ['0', '1', '2'] as const;

export const DEFAULT_TAX_CATEGORY: TaxCategoryKey = 'A1';

/** MwSt.-Satz je Kategorie — gespiegelt von backend/src/events/types/Event.ts's TAX_CATEGORY_PERCENT. */
export const TAX_CATEGORY_PERCENT: Record<TaxCategoryKey, number> = { A0: 0, A1: 19, A2: 7 };

// Anmeldung: null-Channel => individueller Text in Beschreibung
export type RegistrationChannel =
  | 'online'
  | 'email'
  | 'phone'
  | 'in-depot'
  | 'phone-or-email';
// Zahlung: null-Method => individueller Text in Beschreibung
export type PaymentMethod = 'depot' | 'depot_or_transfer' | 'transfer';

export interface EventType {
  id: string;
  name: string;
  /**
   * False for retired/test types. They still appear in the list so an existing
   * event can resolve its name, but the form must not offer them.
   */
  published?: boolean;
  /**
   * Whether a VA of this type is also advertised via a mailing's SECOND
   * period, in addition to the normal period-1 match — see
   * features/events/mailing-match.ts, the only consumer.
   */
  hasExtraPeriod?: boolean;
  /**
   * Central-only ("zentralseitig") VA-Typ (24.09.2026): depot-scoped roles
   * neither see it in the VA-Typ dropdown nor may keep a central-origin event
   * on it — see EventFormDialog.tsx's `frozenDueToOrigin` and EventDataTable's
   * `canEditThisRow`.
   */
  hostedByCentral?: boolean;
}

// SalesChannel für die Kontingent-Erzeugung. v1: Platzhalter (TBD Business-Team).
export interface SalesChannel {
  id: string; // commercetools Channel-key
  name: string;
}

export interface PresetText {
  id: string;
  name: string;
  html: string; // vorgefertigter Veranstaltungstext
}

// Veranstaltungstext = pflegbare Vorlage für den VA-Beschreibungstext.
// Ersetzt die drei fest im Code stehenden PresetText-Beispiele: dieselbe Aufgabe, aber
// vom Fachbereich pflegbar (GET/PUT /api/event-texts).
//
// `category` ist FREITEXT. Der VA-Dialog fasst gleich lautende Werte zu einer Auswahl
// zusammen; ein eigenes Pflegewerkzeug für Kategorien gibt es bewusst nicht. Folge:
// „standard" und „Standard" ergeben zwei Einträge — deshalb schlägt das Formular
// vorhandene Werte vor, legt aber nichts stillschweigend zusammen.
export interface EventText {
  id: string; // serverseitige UUID
  category: string; // Freitext, gruppiert die Auswahl
  title: string; // Anzeigename in der Vorlagen-Auswahl
  text: string; // HTML aus dem Editor (serverseitig bereinigt)
  createdAt: string;
  modifiedAt: string;
  modifiedBy?: string;
  /** E-Mail des Anlegenden. Fehlt bei Bestandsvorlagen. Reine Nachvollziehbarkeit. */
  createdBy?: string;
  /** Rolle des Anlegenden. Fehlt bei Bestandsvorlagen. */
  createdByRole?: string;
  /**
   * Partnernummer, zu der die Vorlage gehört — **steuert den Zugriff** (US-204).
   *
   * Leer heißt „Vorlage der Zentrale": für alle sichtbar, aber nur von Zentrale und Admin
   * änderbar. Gilt auch für Bestandsvorlagen, die das Feld nicht haben.
   *
   * ⚠️ Der Server filtert die Liste bereits und weist unerlaubte Änderungen mit 403 ab.
   * Was die Oberfläche damit macht, ist Bequemlichkeit — keine Absicherung.
   */
  ownerPartnerKey?: string;
  /**
   * Fassungsstand vom Server. Wird beim Speichern zurückgeschickt, damit der Server
   * erkennt, ob zwischenzeitlich jemand anderes geschrieben hat.
   */
  etag?: string;
}

// Anlegen oder Ändern einer Vorlage (PUT /api/event-texts).
// Ohne `id` wird angelegt. `etag` ist der Fassungsstand, den der Bearbeiter geladen hat:
// stimmt er nicht mehr, antwortet der Server mit 409 statt zu überschreiben — eine
// Vorlage entsteht über Minuten im Editor, ein stiller Verlust wäre teuer.
// Zeitstempel und `modifiedBy` fehlen absichtlich: die setzt der Server.
export interface SaveEventTextRequest {
  id?: string;
  category: string;
  title: string;
  text: string;
  etag?: string;
  /**
   * Neue Partnerzuordnung — nur beim ÄNDERN wirksam, nur die Zentrale darf es setzen
   * (US-207). Der Server weist es sonst mit 403 ab.
   *
   * Dreiwertig, absichtlich nicht weglassbar wie die anderen Felder: `undefined` heißt
   * „nicht anfassen", `null` oder `''` heißt „auf keinen Partner setzen" (= Vorlage der
   * Zentrale, OF-10), ein Text ist die neue Partnernummer. Ein simples `string |
   * undefined` könnte „nicht anfassen" nicht von „leeren" unterscheiden.
   */
  ownerPartnerKey?: string | null;
}

/** Ein aktiver Depot-Partner, für die Auswahl beim Umordnen (US-207). */
export interface Partner {
  id: string;
  key: string;
  email?: string;
}

/** Ergebnis eines Löschauftrags, dreigeteilt wie bei den Mailings. */
export interface EventTextDeleteResult {
  removed: string[];
  notFound: string[];
  failed: { id: string; reason: string }[];
}

/**
 * Wie viele Veranstaltungen verweisen je Vorlage darauf?
 *
 * `available: false` heißt, die Zählung war nicht möglich (commercetools nicht
 * erreichbar). Der Löschdialog erscheint dann ohne Zahl — die Warnung ist eine
 * Information, keine Sperre.
 */
export interface EventTextUsageResponse {
  usage: Record<string, number>;
  available: boolean;
}

// Stornobedingungen (PRD-020 Thema 3) — strukturell eine Kopie der Textvorlagen, ohne
// `category` (Entscheidung 3.1: flache Liste, keine Kaskade). Siehe EventText für die
// Begründung jedes einzelnen Felds.
export interface CancellationTerm {
  id: string;
  title: string;
  // Reiner Text, kein HTML (Storno-Folge) — dasselbe Muster wie registrationNote/
  // paymentNote am Event.
  text: string;
  createdAt: string;
  modifiedAt: string;
  modifiedBy?: string;
  createdBy?: string;
  createdByRole?: string;
  /** Steuert den Zugriff wie ownerPartnerKey bei EventText (US-306). */
  ownerPartnerKey?: string;
  etag?: string;
}

// Anlegen oder Ändern einer Stornobedingung (PUT /api/cancellation-terms).
// Wortgleich zu SaveEventTextRequest, ohne `category`.
export interface SaveCancellationTermRequest {
  id?: string;
  title: string;
  text: string;
  etag?: string;
  ownerPartnerKey?: string | null;
}

export interface CancellationTermDeleteResult {
  removed: string[];
  notFound: string[];
  failed: { id: string; reason: string }[];
}

/** Wie viele Veranstaltungen verweisen je Bedingung darauf? Wortgleich zu EventTextUsageResponse. */
export interface CancellationTermUsageResponse {
  usage: Record<string, number>;
  available: boolean;
}

// Zahlungstext-Vorlagen (Rich-Text-Umstellung, Teil 3) — strukturell eine vereinfachte
// Kopie der Stornobedingungen, OHNE Depot-Zuordnung: es gibt keine Partner-Vorlagen,
// nur Zentrale/Admin dürfen überhaupt schreiben (Nutzerentscheidung). Ersetzt die
// bisherige statische Vorschlagsliste (GET /api/payment-note-defaults).
//
// `paymentMethod` (15.09.2026): das VA-Formular übernimmt den Zahlungstext seither
// automatisch anhand der gewählten Zahlungsart statt über eine manuelle Vorlagenwahl
// — siehe EventFormDialog.tsx. Mehrere Vorlagen dürfen dieselbe Zahlungsart tragen,
// keine Eindeutigkeitsprüfung.
export interface PaymentNoteTemplate {
  id: string;
  title: string;
  text: string;
  paymentMethod?: PaymentMethod;
  createdAt: string;
  modifiedAt: string;
  modifiedBy?: string;
  etag?: string;
}

// Anlegen oder Ändern einer Zahlungstext-Vorlage (PUT /api/payment-note-templates).
export interface SavePaymentNoteTemplateRequest {
  id?: string;
  title: string;
  text: string;
  paymentMethod?: PaymentMethod;
  etag?: string;
}

export interface PaymentNoteTemplateDeleteResult {
  removed: string[];
  notFound: string[];
  failed: { id: string; reason: string }[];
}

/** Ein Eintrag der Pflege "Finanzielle Unterstützung" (DCP-2228 follow-up, 14.09.2026). */
export interface FinancialSupport {
  id: string;
  name: string;
  audience: EventAudience;
  /** Pauschale in € (netto), kein Cent-Wert. */
  flatRateAmount: number;
  /** EventType.id-Werte. */
  vaTypes: string[];
  /** Nachweis vom Depot erforderlich (Auftragsbestätigung/Teilnehmernachweis)? */
  proofRequired: boolean;
  createdAt: string;
  createdBy?: string;
  modifiedAt: string;
  modifiedBy?: string;
}

// Anlegen oder Ändern eines Eintrags (PUT /api/financial-support).
export interface SaveFinancialSupportRequest {
  id?: string;
  name: string;
  audience: EventAudience;
  flatRateAmount: number;
  vaTypes: string[];
  proofRequired: boolean;
}

export interface FinancialSupportDeleteResult {
  removed: string[];
  notFound: string[];
  failed: { id: string; reason: string }[];
}

/** Wie viele Veranstaltungen verweisen je Vorlage darauf? Wortgleich zu CancellationTermUsageResponse. */
export interface PaymentNoteTemplateUsageResponse {
  usage: Record<string, number>;
  available: boolean;
}

// Anmeldetext-Vorlagen — wortgleich zu PaymentNoteTemplate, siehe dort für die
// Begründung. Ersetzt GET /api/registration-note-defaults.
export interface RegistrationNoteTemplate {
  id: string;
  title: string;
  text: string;
  createdAt: string;
  modifiedAt: string;
  modifiedBy?: string;
  etag?: string;
}

export interface SaveRegistrationNoteTemplateRequest {
  id?: string;
  title: string;
  text: string;
  etag?: string;
}

export interface RegistrationNoteTemplateDeleteResult {
  removed: string[];
  notFound: string[];
  failed: { id: string; reason: string }[];
}

export interface RegistrationNoteTemplateUsageResponse {
  usage: Record<string, number>;
  available: boolean;
}

// Mailing = Katalog-Aussendung, in der eine VA bewerbbar ist.
// Quelle ist eine Excel-Liste, die ein Admin hochlädt (POST /api/mailings/import).
// Beide Daten sind reine Tage (YYYY-MM-DD), ohne Uhrzeit — die Vorlage hat keine.
export interface Mailing {
  key: string; // Bezeichnung, z.B. "JAN A" — Identität
  title: string; // Anzeigetext (heute identisch mit key)
  period1From: string; // Zeitraum 1 von
  period1To: string; // Zeitraum 1 bis
  period2From?: string; // Zeitraum 2 von — optional, nur für Kategorien mit längerer Vorlaufzeit
  period2To?: string; // Zeitraum 2 bis — optional, immer zusammen mit period2From
  onlineDate: string; // Onlinestellung spätestens
  cutoffDate: string; // Eingabe spätestens — letzter Abgabetag
  deliveryDate: string; // Versanddatum des Mailings
}

/** Ergebnis eines Mailing-Imports (nur für die Admin-Ansicht). */
export interface MailingImportResult {
  imported: number;
  removed: number;
  skipped: number;
  errors: { row: number; reason: string }[];
}

// Anlegen oder Ändern eines einzelnen Mailings (PUT /api/mailings).
// `previousTitle` ist die Identität des zu ersetzenden Eintrags und fehlt beim
// Anlegen. Wird die Bezeichnung geändert, ist das serverseitig Neu-Anlegen +
// Alt-Löschen: der commercetools-Schlüssel wird aus der Bezeichnung abgeleitet
// und wandert dabei mit.
export interface SaveMailingRequest {
  previousTitle?: string;
  title: string;
  period1From: string;
  period1To: string;
  period2From?: string;
  period2To?: string;
  onlineDate: string;
  cutoffDate: string;
  deliveryDate: string;
}

// Ergebnis eines Löschauftrags, dreigeteilt.
// `notFound` steht bewusst neben `failed` und nicht darin: ein Eintrag, der schon
// weg war, ist das gewünschte Ergebnis — ein Doppelklick darf nicht wie ein
// Fehler aussehen. `failed` nennt den Grund je Eintrag, weil es über die
// commercetools-Objekte keine Transaktion gibt und ein Teilfehler sichtbar
// bleiben muss.
export interface MailingDeleteResult {
  removed: string[];
  notFound: string[];
  failed: { title: string; reason: string }[];
}

// VA-Massenimport (05.08.2026). Mirrors backend/src/events/import/import-status.ts's
// ImportJobStatusFile — same field names, so no translation layer is needed.
export type ImportJobStatus =
  | 'pending' // hochgeladen, noch nicht gestartet
  | 'running'
  | 'stopped' // stopRequested wurde befolgt
  | 'completed' // bis zum Dateiende gelaufen
  | 'failed' // Worker abgestürzt (kein Zeilenfehler — siehe errors)
  | 'rolled_back'; // alle erstellten VAs wurden über die Rückgängig-Funktion gelöscht

export interface ImportRowError {
  row: number;
  reason: string;
}

export interface ImportJobStatusFile {
  jobId: string;
  filename: string;
  vaType: string;
  targetStatus: 'initial' | 'approved';
  status: ImportJobStatus;
  total: number;
  processed: number;
  created: string[];
  errors: ImportRowError[];
  stopRequested: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Antwort von POST /api/events/import. */
export interface UploadEventsImportResult {
  jobId: string;
  total: number;
}

/** Antwort von POST /api/events/import/upload-url (18.-20.09.2026). */
export interface UploadUrlResult {
  jobId: string;
  uploadUrl: string;
}

/** Antwort von POST /api/events/import/:jobId/rollback. */
export interface RollbackImportResult {
  removed: string[];
  failed: { identifier: string; reason: string }[];
}

// Benutzerverwaltung. Seit PRD-021 führt sie bei ROLLE und RECHTEN für alle vier Rollen;
// commercetools führt weiter bei Depots und Vertragsfenster. Depot-Rollen sind deshalb
// speicherbar — vergeben werden sie aber nicht per Hand, sie entstehen beim ersten Login.
export type ManagedRole = 'zentral' | 'admin' | 'partner' | 'mitarbeiter';

/** Ob ein Admin über den Eintrag entschieden hat. */
export type ApprovalState = 'pending' | 'approved';

export interface ManagedUser {
  email: string;
  role: ManagedRole;
  /** Aktionen je App — offen, damit neue Apps keine Code-Änderung brauchen. */
  permissions: Record<string, string[]>;
  /**
   * `pending` = automatisch beim ersten Login angelegt, wartet auf einen Admin.
   * `approved` = ein Admin hat entschieden; leere Rechte heißen dann „darf nichts".
   *
   * Fehlt bei Altbestand und gilt dann als `approved` — der Server setzt es beim Lesen.
   */
  approvalState?: ApprovalState;
  /**
   * Abweichende Rolle aus commercetools, nur wenn sie von `role` abweicht.
   *
   * ⚠️ Wird pro Aufruf ermittelt und **nicht gespeichert**. Die gespeicherte Rolle bleibt
   * führend; dies ist ein Hinweis für den Admin, keine Vorgabe. Fehlt, wenn commercetools
   * nicht erreichbar war — die Liste kommt dann trotzdem.
   */
  ctRole?: string;
  /** Darf zum Testen die Rolle wechseln (nur wo die Umgebung es erlaubt). */
  canSwitchRole?: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

/** Welche Apps es gibt und welche Aktionen sie kennen — treibt die Ankreuzfelder. */
export type PermissionCatalog = Record<string, string[]>;

/** Alle vier Rollenwerte, die in der Benutzerliste vorkommen können — auch die
 * automatisch angelegten Depot-Rollen, nicht nur die per Hand vergebbaren. */
export const MANAGED_ROLES: readonly ManagedRole[] = ['admin', 'zentral', 'partner', 'mitarbeiter'];

/** Filter für die Benutzerverwaltung — rein client-seitig, die Liste ist überschaubar. */
export interface UserFilters {
  role?: ManagedRole;
  /** Teilstring-Suche auf der E-Mail-Adresse, klein geschrieben verglichen. */
  search?: string;
}

// Ticketliste einer VA — echte Ticketkäufer aus der ticket-api. Keine
// Namensauflösung mehr (25.09.2026, ticket-api hat ihr Participants-API
// abgeschafft) — nur für die Abrechnung intern genutzt, seit die
// standalone-Teilnehmerliste (ParticipantsDialog.tsx) entfernt wurde.
export interface Participant {
  /** Ticket-Id, stabiler Schlüssel für die Tabelle. */
  identifier: string;
  /** Leer bei anonymem Verkauf oder Verkauf über die Theke. */
  customerNumber: string;
  reserved: boolean;
  sold: boolean;
  redeemed: boolean;
  /** Soft-Storno der ticket-api; fehlt bei älteren Antworten. */
  cancelled?: boolean;
  anonymous: boolean;
  /** Kaufdatum. */
  createdAt: string;
}

export interface ParticipantsResponse {
  items: Participant[];
  total: number;
}

/** Antwort von POST /api/session/role. */
export interface RoleSwitchResult {
  token: string;
  role: Role;
  depots: string[];
  expiresAt: number;
}

export interface DepotEvent {
  identifier: string; // uuid
  /** Commercetools-Objekt-ID — dieselbe UUID, die auch die ticket-api als event_id verwendet. NICHT identisch mit identifier (das ist bei CT-gestützten Events object.key, siehe backend depot-event-mapper.ts). */
  ctId: string;
  createdAt: string; // ISO-8601
  updatedAt: string;
  /** E-Mail des Anlegenden. `'six-cms'` bei den importierten Bestandsobjekten. */
  createdBy: string;
  /** E-Mail der Person, die zuletzt geändert hat. */
  modifiedBy: string;
  /**
   * Rolle des Anlegenden, in vier Werten.
   *
   * Feiner als `origin`, das Admin+Zentrale zu „central" und Partner+Mitarbeiter zu
   * „partner" zusammenfasst. Bei den ~13.000 Bestandsobjekten `undefined` — dort nicht
   * rekonstruierbar und deshalb bewusst nicht geraten.
   */
  createdByRole?: Role;
  /**
   * "v1" = Altdaten aus dem alten six-cms-Weg, "v2" = über admin-app selbst
   * angelegt (DCP-2228 follow-up, 18.09.2026). Reiner Systemstempel, vom
   * Formular nie geschrieben — siehe EventFormDialog.tsx's `toForm()`, das
   * ihn beim Bearbeiten explizit herausfiltert.
   */
  eventsVersion?: 'v1' | 'v2';
  status: EventStatus;
  origin: EventOrigin;
  depots: DepotRef[]; // Herkunfts-Depot(s)
  promotingDepots: DepotRef[]; // bewerbende Depots (frei wählbar)
  vaType: string; // references EventType.id
  title: string;
  teaser?: string; // Teaserzeile (unter Titel)
  /**
   * "Sprechende URL" für den Storefront-Pfad (DCP-2228 follow-up, 18.09.2026)
   * — ersetzt dort langfristig die UUID (SEO). Automatisch aus Titel + Datum
   * vorgeschlagen, solange das Feld noch leer ist; nur Admin darf es danach
   * überschreiben. Muss `^[a-zA-Z0-9_-]{2,256}$` entsprechen und ist je
   * Depot eindeutig — beides serverseitig geprüft (event-input.ts /
   * CtEventRepository.ts).
   */
  slug?: string;
  description?: string; // Veranstaltungstext (HTML aus RichText-Editor)
  presetTextId?: string; // vorgefertigter VA-Text (references PresetText.id)
  audience: EventAudience;
  // Datum/Zeit: Beginn + Ende als datetime; businessHours deaktiviert Uhrzeit-Anteil
  startAt: string; // ISO-8601 datetime
  endAt: string; // ISO-8601 datetime
  businessHours: boolean; // true => "während der Geschäftszeiten" (Uhrzeit irrelevant)
  // Kostenbeitrag
  costEnabled: boolean; // true = kostenpflichtig, false = kostenfrei
  priceAmount?: number; // Kostenbeitrag in € (brutto), nur wenn costEnabled
  // Vorteilspreis Kundenkarte in € (brutto), nur wenn costEnabled. `null` im Request heißt
  // „entfernen“ (ein fehlender Schlüssel lässt den gespeicherten Wert unberührt).
  advantagePriceAmount?: number | null;
  priceUnit: PriceUnit; // package | person (default person) — gilt für beide Preise
  taxCategory?: TaxCategoryKey; // A1 (19 %) wenn nicht gesetzt
  // Anmeldung
  registrationRequired: boolean;
  registrationChannel?: RegistrationChannel | null; // null => individueller Text in Beschreibung
  registrationDeadline?: string; // Datum
  // Beim Auswählen einer Vorlage (GET /api/registration-note-templates) KOPIERT,
  // danach frei editierbar — dasselbe Muster wie cancellationNote/presetTextId/
  // description. Unabhängig von registrationChannel.
  registrationNote?: string;
  /** Verweis auf RegistrationNoteTemplate.id, nur für die Verwendungszählung. */
  registrationNoteTemplateId?: string;
  // Zahlung
  paymentMethod?: PaymentMethod | null; // null => individueller Text in Beschreibung
  paymentDeadline?: string; // Datum
  // Beim Auswählen einer Vorlage (GET /api/payment-note-templates) KOPIERT, danach
  // frei editierbar. Dasselbe Muster wie registrationNote, unabhängig von paymentMethod.
  paymentNote?: string;
  /** Verweis auf PaymentNoteTemplate.id, nur für die Verwendungszählung. */
  paymentNoteTemplateId?: string;
  // Überweisungsdaten — reine Kontoangaben, nicht lokalisiert (anders als paymentNote):
  // eine IBAN oder ein Kontoinhaber-Name ändert sich nicht je Sprache. Alle vier
  // optional, weil nicht jede Zahlungsart eine Überweisung ist.
  paymentAccountHolder?: string;
  paymentIban?: string;
  paymentBic?: string;
  /** Verwendungszweck, z. B. eine VA- oder Buchungsnummer. */
  paymentReference?: string;
  /**
   * Ob die Kontodaten in der Bestätigungsmail erscheinen (15.09.2026). Default
   * `true` in emptyForm() — bei `false` bleiben die Kontofelder im Formular
   * unverändert stehen, nur ausgeblendet.
   */
  publishAccountData?: boolean;
  /**
   * Verweis auf CancellationTerm.id (US-304/305, PRD-020 Thema 3).
   *
   * `cancellationNote` trägt den beim Auswählen KOPIERTEN Wortlaut — eine spätere
   * Änderung an der Bedingung wirkt bewusst NICHT auf bestehende VAs (Entscheidung OF-7,
   * dasselbe Muster wie presetTextId/description). Reiner Text, kein HTML — dasselbe
   * Muster wie registrationNote/paymentNote.
   */
  cancellationTermsId?: string;
  cancellationNote?: string;
  // Kontingent — liegt in der ticket-api, NICHT am Event.
  //
  // In commercetools steht nur `ticketed` als Marker, dass ein Kontingent
  // existiert. qty/available/sold/salesChannels/taxCategory liefert das Backend
  // beim Lesen aus dem Kontingent nach und schreibt sie direkt dorthin zurück —
  // so gibt es genau eine Wahrheit über die Platzzahl.
  ticketed?: boolean;
  qty?: number;
  available?: number; // read-only, von der ticket-api berechnet
  sold?: number; // read-only, von der ticket-api berechnet
  /** Refund-Status aus der ticket-api, unset wenn noch keine Abrechnung existiert — read-only (21.09.2026-Follow-up, "Abrechnung"-Spalte). */
  refundStatus?: TicketRefundStatus;
  salesChannels?: string[];
  // Veranstaltungsort
  locationType: LocationType;
  externalName?: string; // if locationType === 'external' (Abweichender Veranstaltungsort)
  externalStreet?: string;
  externalHouseNo?: string;
  externalZip?: string;
  externalCity?: string;
  externalCountry?: string;
  externalLogoUrl?: string;
  externalLink?: string;
  // Sonstiges
  plannedOnlineDate?: string; // d.m.Y — geplanter Zeitpunkt Onlinestellung
  rejectionReason?: string; // set when central rejects (in_review -> initial)
  /**
   * Mailing.key(s) der beim Anlegen passenden Mailings — leer ohne Treffer.
   * Serverseitig einmal berechnet, kein Feld zum Setzen durch den Client — siehe
   * CreateEventRequest, das es ausdrücklich ausschließt. Normalerweise ein
   * Eintrag; mit zwei, wenn der VA-Typ `hasExtraPeriod` gesetzt hat und auch
   * ein Zeitraum-2-Treffer existiert.
   */
  availableMailings?: string[];

  /**
   * Freigabe der automatisch errechneten finanziellen Unterstützung durch
   * Admin/Zentrale (14.09.2026) — reine Anzeige+Checkbox im VA-Formular, der
   * Betrag selbst wird nicht hier gespeichert, nur ob er freigegeben wurde.
   */
  financialSupportApproved?: boolean;

  /**
   * Ob bei dieser VA Glas- oder Gedeckverkauf stattfand (DCP-2228 follow-up,
   * 16.09.2026) — eine der beiden Voraussetzungen für die Weinkostenübernahme
   * in der Abrechnung. Bis dahin Teil des Abrechnungs-Wizards
   * (invertiert, "Kein Glas- oder Gedeckverkauf"), jetzt Teil der VA-Eingabe:
   * eine Tatsache über die VA selbst, keine Abrechnungs-Arbeit — normale
   * Formularsperre, von jeder Rolle pflegbar.
   */
  glassOrTableSale?: boolean;

  /**
   * Nur bei Teilnehmerkreis "geschlossen" im Formular sichtbar/gepflegt
   * (15.09.2026) — siehe EVENT_REASON_OPTIONS.
   */
  eventReason?: string;
  /** Auftraggeber der geschlossenen Veranstaltung, Freitext (15.09.2026). */
  eventClient?: string;
}

export interface EventFilters {
  status?: EventStatus[];
  origin?: EventOrigin[];
  audience?: EventAudience[];
  depotKeys?: string[];
  vaTypes?: string[]; // EventType.id
  year?: number;
  month?: number;
  dateFrom?: string;
  dateTo?: string;
  /** Nur Events, deren endAt-Tag VOR diesem Datum liegt — reiner "schon vorbei"-Filter, anders als dateFrom/dateTo (Bereichsüberlappung). Partner-Schnellzugriff "Veranstaltungen abschließen" (22.09.2026-Follow-up). */
  endedBefore?: string;
  search?: string; // title prefix search (v1: no full-text)
}

export interface EventListParams extends EventFilters {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** Ticket-api Refund-Status, post-gefiltert im Backend (21.09.2026-Follow-up, "Abrechnung zu prüfen"). Kein DepotEvent-Feld, deshalb hier statt in EventFilters. */
  refundStatus?: string[];
  /** Nur VAs mit ticket-api-Verfügbarkeit unter 10% (25.09.2026, "Kontingent prüfen"). Post-gefiltert im Backend, kein DepotEvent-Feld. */
  lowContingent?: boolean;
}

// Create/Update payloads: everything except server-managed metadata.
//
// `available`, `sold` und `ticketed` sind ausgeschlossen: die erste beiden
// berechnet die ticket-api, das Flag setzt das Backend aus qty. Mitsenden hätte
// keine Wirkung und würde suggerieren, der Client könnte sie bestimmen.
export type CreateEventRequest = Omit<
  DepotEvent,
  | 'identifier'
  | 'ctId'
  | 'createdAt'
  | 'updatedAt'
  | 'createdBy'
  | 'createdByRole'
  | 'modifiedBy'
  | 'status'
  | 'available'
  | 'sold'
  | 'ticketed'
  | 'refundStatus'
  // Server-computed from the mailing list at creation time — a client-supplied
  // value would let it claim eligibility it never had.
  | 'availableMailings'
  // System stamp, set only by CtEventRepository.create() (DCP-2228 follow-up,
  // 18.09.2026) — see DepotEvent.eventsVersion.
  | 'eventsVersion'
>;

export interface UpdateEventRequest extends Partial<CreateEventRequest> {
  identifier: string;
}

/**
 * One occurrence of a VA-Serie (24.09.2026, Nutzervorgabe) — everything else
 * on the resulting event is cloned from the source; only these four fields
 * vary per occurrence.
 */
export interface EventSeriesOccurrence {
  startAt: string;
  endAt: string;
  /** Pro Termin wählbar (25.09.2026, Nutzervorgabe) — unabhängig vom Quell-Event. */
  businessHours: boolean;
  /** Only meaningful (and required server-side) when the source has registrationRequired. */
  registrationDeadline?: string;
  /** Only meaningful (and required server-side) when the source has costEnabled. */
  paymentDeadline?: string;
  plannedOnlineDate: string;
}

export interface CreateEventSeriesRequest {
  occurrences: EventSeriesOccurrence[];
  /** 'initial' = "Speichern", 'in_review' = "Zur Prüfung", per occurrence. */
  createAs: 'initial' | 'in_review';
}

export interface CreateEventSeriesResult {
  created: DepotEvent[];
  failed: { index: number; message: string }[];
}

/** Per-occurrence validation errors from POST /api/events/:id/series (400). */
export interface EventSeriesValidationError {
  error: { code: 'OCCURRENCE_VALIDATION_FAILED'; message: string };
  occurrenceErrors: { index: number; errors: Record<string, string> }[];
}

export interface SubmitEventRequest {
  identifier: string;
}
export interface ApproveEventRequest {
  identifier: string;
}
export interface RejectEventRequest {
  identifier: string;
  reason: string; // Pflichtgrund
}
export interface ReopenEventRequest {
  identifier: string;
  toStatus?: EventStatus.InReview | EventStatus.Initial; // default: in_review
}

/** One advertising depot the notification mail could not be sent to, and why. */
export interface NotifyAdvertisingDepotsFailure {
  depotKey: string;
  organizingDepotKey: string;
  reason: string;
}

/** Response of POST /events/:id/notify-advertising-depots. */
export interface NotifyAdvertisingDepotsResult {
  /** "<organizingKey>-><advertisingKey>" per successfully sent mail. */
  sent: string[];
  failed: NotifyAdvertisingDepotsFailure[];
}


// Auth Types
export enum Role {
  Partner = 'partner',
  Mitarbeiter = 'mitarbeiter',
  Zentral = 'zentral',
  Admin = 'admin',
}

export interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
  // Veranstaltungen feature (additive, optional — reviews unaffected):
  role?: Role; // primary role for the events feature
  depots?: DepotRef[]; // depots the user is assigned to (partners: 1..n)
  /**
   * Partnernummer der Depot-Rolle, sofern auflösbar.
   *
   * Für die Vorlagenpflege: sie entscheidet damit ohne zweiten Abruf, welche Vorlagen
   * bearbeitbar sind. ⚠️ Anzeigehilfe — der Server prüft dieselbe Regel selbst.
   */
  partnerKey?: string;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

// UI Types
export interface DataTableColumn<T> {
  id: string;
  header: string;
  accessorKey?: keyof T;
  cell?: (row: T) => React.ReactNode;
  enableSorting?: boolean;
  enableFiltering?: boolean;
}

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: 'default' | 'destructive';
}

// --- Abrechnung / Refund (DCP-2228, 08.09.2026) -------------------------------
//
// Money/Price and the Ticket*-prefixed types mirror the ticket-api's own wire
// format verbatim (snake_case) — the admin-app backend passes them through
// unchanged, same convention as TicketContingent in the backend's
// ticket-api-client.ts. RefundDraft* types are the admin-app's own (camelCase,
// like everything else here) — they never touch the ticket-api directly.

export interface Money {
  centAmount: number;
  currencyCode: string;
  fractionDigits?: number;
}

export interface Price {
  netto: Money;
  brutto: Money;
}

export type TicketRefundStatus = 'new' | 'in_review_proof' | 'in_review' | 'reviewed' | 'refunded';
export type TicketRefundItemType = 'ticket' | 'product';

export interface TicketRefund {
  identifier: string;
  event_id: string;
  status: TicketRefundStatus;
  amount_total: Price;
  refund_calculated: Price;
  created_at: string;
  created_by: string;
  modified_at?: string;
  modified_by?: string;
}

export interface TicketRefundItem {
  id: string;
  refund_id: string;
  type: TicketRefundItemType;
  qty: number;
  refund: Price;
  price: Price;
  tax_category: TaxCategoryKey;
  sku?: string;
  ticket_id?: string;
}

export interface RefundDraftParticipant {
  ticketId: string;
  customerNumber: string;
  name?: string;
  /** Nur aus der CDP-Auflösung oder der Nachpflege — commercetools liefert hier keine E-Mail. */
  email?: string;
  /** true bei Nachpflege; steuert u. a. die Sichtbarkeit des Löschen-Icons für Depot-Partner. */
  manuallyAdded?: boolean;
  /** Für die Statusanzeige (Reserviert/Verkauft/Teilgenommen) in der Abrechnung, wie in der VA-Übersicht. */
  reserved?: boolean;
  sold?: boolean;
  redeemed?: boolean;
  included: boolean;
  /** Storniert über die Abrechnung — Checkbox bleibt deaktiviert, Zeile ausgegraut. */
  cancelled?: boolean;
}

/**
 * Body of POST /api/events/:id/refund/participants — "Tickets nachpflegen".
 * Anonymous-only since 25.09.2026 — named entry (firstName/lastName) was
 * removed along with the ticket-api's Participants API.
 */
export interface AddRefundParticipantInput {
  /** Sammelerfassung ohne Einzelangaben — nur für geschlossene Gesellschaften (21.09.2026-Follow-up). */
  anonymous: true;
}

export interface RefundDraftWine {
  sku: string;
  name: string;
  qtyUsed: number;
  priceGrossCents: number;
  currencyCode: string;
  taxCategory: TaxCategoryKey;
}

export interface RefundDraft {
  eventId: string;
  participants: RefundDraftParticipant[];
  wines: RefundDraftWine[];
  updatedAt: string;
}

export interface RefundView {
  refund: TicketRefund | null;
  items: TicketRefundItem[];
  draft: RefundDraft | null;
  flatRate: { netCents: number };
  /** Aus der passenden "Finanzielle Unterstützung"-Zuordnung, DCP-2228 follow-up 15.09.2026. */
  proofRequired: boolean;
  proof: RefundProofMeta | null;
  /** Ob die automatisierte Übertragung für diese VA schon greift (DCP-2228 follow-up, 16.09.2026). */
  settlementMode: 'manual' | 'automatic';
}

/** Nachweis-Upload in der Abrechnung (DCP-2228 follow-up, 15.09.2026). */
export interface RefundProofMeta {
  eventId: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedBy?: string;
}

export interface WineProduct {
  sku: string;
  name: string;
  priceGrossCents: number;
  currencyCode: string;
  taxCategory: TaxCategoryKey;
}

/** One hit from the CDP customer search, used by the Nachpflege customer picker. */
export interface CustomerSearchResult {
  customerNumber: string;
  firstName?: string;
  lastName?: string;
}

export interface SettlementWineLine {
  sku: string;
  name: string;
  qtyUsed: number;
  qtyEligibleHalfBottles: number;
  qtyChargedHalfBottles: number;
  refund: Price;
  charge: Price;
  taxCategory: TaxCategoryKey;
}

/** Schritt 3.3's numbers — computed by /refund/preview and /refund/submit, never in the frontend. */
export interface Settlement {
  participantCount: number;
  winesQualify: boolean;
  wines: SettlementWineLine[];
  flatRate: Price;
  /** MwSt-Satz der Pauschale — die des Events selbst (Zahlung/taxCategory), 21.09.2026-Follow-up. */
  flatRateTaxCategory: TaxCategoryKey;
  totalCharge: Price;
  finalAmount: Price;
  /** Aus der passenden "Finanzielle Unterstützung"-Zuordnung, DCP-2228 follow-up 15.09.2026. */
  proofRequired: boolean;
}


